

interface ProUpgradeSuccessModalProps {
  onStart: () => void;
  onDismiss: () => void;
}

export default function ProUpgradeSuccessModal({ onStart, onDismiss }: ProUpgradeSuccessModalProps) {
  return (
    <div style={BACKDROP}>
      {/* Confetti container */}
      <div style={MODAL_WRAP}>
        {/* Confetti pieces — exact positions from design */}
        {CONFETTI_STYLES.map((s, i) => (
          <div key={i} style={s} />
        ))}

        {/* Modal card */}
        <div style={MODAL_CARD}>
          {/* Orange gradient header */}
          <div style={HEADER}>
            {/* Sparkles */}
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ position: 'absolute', top: 30, left: 80, opacity: 0.5 }}>
              <path d="M8 0L9.5 6.5L16 8L9.5 9.5L8 16L6.5 9.5L0 8L6.5 6.5Z" fill="#FFFFFF" />
            </svg>
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" style={{ position: 'absolute', top: 55, right: 100, opacity: 0.4 }}>
              <path d="M8 0L9.5 6.5L16 8L9.5 9.5L8 16L6.5 9.5L0 8L6.5 6.5Z" fill="#FFFFFF" />
            </svg>
            <svg width="10" height="10" viewBox="0 0 16 16" fill="none" style={{ position: 'absolute', bottom: 30, left: 130, opacity: 0.35 }}>
              <path d="M8 0L9.5 6.5L16 8L9.5 9.5L8 16L6.5 9.5L0 8L6.5 6.5Z" fill="#FFFFFF" />
            </svg>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ position: 'absolute', top: 25, right: 60, opacity: 0.3 }}>
              <path d="M8 0L9.5 6.5L16 8L9.5 9.5L8 16L6.5 9.5L0 8L6.5 6.5Z" fill="#FFFFFF" />
            </svg>

            {/* Checkmark circle */}
            <div style={CHECK_CIRCLE}>
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                <path d="M10 18L16 24L27 12" stroke="#D97645" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </div>

          {/* Text section */}
          <div style={TEXT_SECTION}>
            <span style={EYEBROW}>You're Pro</span>
            <h2 style={TITLE}>Welcome to Corner Pro</h2>
            <p style={SUBTITLE}>Every document tool, unlimited. No caps, no compromises.</p>
          </div>

          {/* Feature rows */}
          <div style={FEATURES}>
            <div style={FEATURE_ROW}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ flexShrink: 0 }}>
                <path d="M9 2L10.5 7L15.5 9L10.5 11L9 16L7.5 11L2.5 9L7.5 7Z" fill="#D97645" />
              </svg>
              <span style={FEATURE_TEXT}>Unlimited AI on every document</span>
            </div>
            <div style={FEATURE_ROW}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ flexShrink: 0 }}>
                <circle cx="9" cy="9" r="7" stroke="#D97645" strokeWidth="1.5" />
                <path d="M6 9L8.5 11.5L12.5 7" stroke="#D97645" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span style={FEATURE_TEXT}>Sign, extract, convert, merge — no limits</span>
            </div>
            <div style={FEATURE_ROW}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ flexShrink: 0 }}>
                <path d="M9 4V14M4 9H14" stroke="#D97645" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <span style={FEATURE_TEXT}>Priority access to new tools as we ship them</span>
            </div>
          </div>

          {/* Actions */}
          <div style={ACTIONS}>
            <button type="button" onClick={onStart} style={CTA_BUTTON}>
              Start using Pro
            </button>
            <button type="button" onClick={onDismiss} style={DISMISS}>
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Confetti — exact positions/colors/rotations from Paper design ───

const C: React.CSSProperties = { position: 'absolute', zIndex: 0 };

const CONFETTI_STYLES: React.CSSProperties[] = [
  // Top row (above card)
  { ...C, width: 10, height: 4, top: -40, left: 30, borderRadius: 2, rotate: '-40deg', backgroundColor: '#1B7A4B' },
  { ...C, width: 8, height: 8, top: -20, left: 60, borderRadius: '50%', backgroundColor: '#D97645', opacity: 0.9 },
  { ...C, width: 7, height: 7, top: -45, left: 130, borderRadius: '50%', backgroundColor: '#4285F4' },
  { ...C, width: 12, height: 5, top: -30, left: 180, borderRadius: 2, rotate: '25deg', backgroundColor: '#E8534A' },
  { ...C, width: 5, height: 12, top: -50, left: 220, borderRadius: 2, rotate: '30deg', backgroundColor: '#E8534A' },
  { ...C, width: 6, height: 14, top: -15, left: 300, borderRadius: 2, rotate: '-15deg', backgroundColor: '#4285F4' },
  { ...C, width: 6, height: 6, top: -48, right: 120, borderRadius: '50%', backgroundColor: '#D97645' },
  { ...C, width: 10, height: 10, top: -35, left: 400, borderRadius: '50%', backgroundColor: '#1B7A4B' },
  { ...C, width: 14, height: 6, top: -10, right: 40, borderRadius: 2, rotate: '45deg', backgroundColor: '#D97645' },
  { ...C, width: 11, height: 4, top: -42, right: 30, borderRadius: 2, rotate: '50deg', backgroundColor: '#1B7A4B' },
  // Left side
  { ...C, width: 7, height: 7, top: 30, left: -20, borderRadius: '50%', backgroundColor: '#E8534A' },
  { ...C, width: 10, height: 4, top: 80, left: -15, borderRadius: 2, rotate: '-30deg', backgroundColor: '#4285F4' },
  { ...C, width: 8, height: 8, top: 150, left: -25, borderRadius: 2, rotate: '60deg', backgroundColor: '#1B7A4B' },
  { ...C, width: 5, height: 5, top: 200, left: -30, borderRadius: '50%', backgroundColor: '#4285F4', opacity: 0.7 },
  // Right side
  { ...C, width: 6, height: 12, top: 40, right: -18, borderRadius: 2, rotate: '20deg', backgroundColor: '#D97645' },
  { ...C, width: 9, height: 9, top: 120, right: -22, borderRadius: '50%', backgroundColor: '#E8534A' },
  { ...C, width: 12, height: 5, top: 200, right: -15, borderRadius: 2, rotate: '-45deg', backgroundColor: '#4285F4' },
  { ...C, width: 8, height: 3, top: 260, right: -28, borderRadius: 2, rotate: '-25deg', backgroundColor: '#D97645', opacity: 0.7 },
  // Bottom row (below card)
  { ...C, width: 8, height: 8, bottom: -20, left: 100, borderRadius: '50%', backgroundColor: '#1B7A4B' },
  { ...C, width: 10, height: 4, bottom: -25, left: 250, borderRadius: 2, rotate: '35deg', backgroundColor: '#D97645' },
  { ...C, width: 12, height: 5, bottom: -30, right: 160, borderRadius: 2, rotate: '-20deg', backgroundColor: '#4285F4' },
  { ...C, width: 6, height: 6, bottom: -15, right: 80, borderRadius: '50%', backgroundColor: '#E8534A' },
];

// ─── Styles ─────────────────────────────────────

const BACKDROP: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(26, 23, 20, 0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 200,
};

const MODAL_WRAP: React.CSSProperties = {
  position: 'relative',
  width: 480,
};

const MODAL_CARD: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: 480,
  borderRadius: 20,
  overflow: 'clip',
  backgroundColor: '#FDFCFB',
  boxShadow: 'rgba(0, 0, 0, 0.25) 0px 24px 80px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px',
  position: 'relative',
  zIndex: 1,
};

const HEADER: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: 160,
  position: 'relative',
  backgroundImage: 'linear-gradient(135deg, #D4804A 0%, #E0A070 50%, #D4804A 100%)',
  flexShrink: 0,
};

const CHECK_CIRCLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 72,
  height: 72,
  borderRadius: '50%',
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 20px',
};

const TEXT_SECTION: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  paddingTop: 32,
  paddingInline: 40,
  paddingBottom: 20,
  gap: 6,
};

const EYEBROW: React.CSSProperties = {
  fontSize: 12,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: '#D97645',
  fontFamily: '"Geist", system-ui, sans-serif',
  fontWeight: 600,
  lineHeight: '16px',
};

const TITLE: React.CSSProperties = {
  margin: 0,
  fontSize: 32,
  textAlign: 'center',
  lineHeight: '38px',
  color: '#2E2318',
  fontFamily: '"Instrument Serif", Georgia, serif',
  fontWeight: 400,
};

const SUBTITLE: React.CSSProperties = {
  margin: 0,
  fontSize: 15,
  textAlign: 'center',
  lineHeight: '24px',
  color: '#6B5E52',
  fontFamily: '"Geist", system-ui, sans-serif',
  fontWeight: 300,
};

const FEATURES: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  paddingTop: 8,
  paddingInline: 40,
  paddingBottom: 28,
  gap: 12,
  boxSizing: 'border-box',
};

const FEATURE_ROW: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  borderRadius: 10,
  paddingBlock: 12,
  paddingInline: 16,
  gap: 12,
  backgroundColor: '#F5F2ED',
};

const FEATURE_TEXT: React.CSSProperties = {
  fontSize: 14,
  color: '#3D3229',
  fontFamily: '"Geist", system-ui, sans-serif',
  fontWeight: 400,
  lineHeight: '20px',
};

const ACTIONS: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  gap: 14,
  paddingInline: 40,
  paddingBottom: 32,
  boxSizing: 'border-box',
};

const CTA_BUTTON: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  borderRadius: 10,
  paddingBlock: 14,
  border: 'none',
  backgroundColor: '#2E2318',
  cursor: 'pointer',
  fontSize: 15,
  fontWeight: 600,
  color: '#FDFCFB',
  fontFamily: '"Geist", system-ui, sans-serif',
  lineHeight: '18px',
};

const DISMISS: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 400,
  color: '#9A8E82',
  fontFamily: '"Geist", system-ui, sans-serif',
  lineHeight: '16px',
  padding: 0,
};
